import React, { useState, useEffect } from 'react';
import {
  Users, Mail, Plus, Trash2, Search, Loader2,
  Check, X, UserPlus, Bell
} from 'lucide-react';

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

export default function Team({ darkMode }) {
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members'); // 'members' or 'requests'

  // Invite State
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [notification, setNotification] = useState({ type: '', msg: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTeam(data.team || []);
        setInvites(data.invites || []);
        // Auto-switch to requests tab if you have pending invites
        if (data.invites && data.invites.length > 0) setActiveTab('requests');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, msg) => {
    setNotification({ type, msg });
    setTimeout(() => setNotification({ type: '', msg: '' }), 4000);
  };

  // --- ACTIONS ---
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setIsInviting(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/team/invite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail })
      });
      const data = await res.json();

      if (res.ok) {
        showNotification('success', 'Invitation sent! They need to accept it.');
        setInviteEmail('');
      } else {
        showNotification('error', data.error || 'Failed to send invite.');
      }
    } catch (err) {
      showNotification('error', 'Network Error');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRespond = async (inviteId, action) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/team/respond`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ inviteId, action })
      });
      const data = await res.json();
      if (res.ok) {
        // Remove from invites list locally
        setInvites(invites.filter(inv => inv._id !== inviteId));
        if (action === 'accept') {
          setTeam(data.team); // Update team list
          showNotification('success', 'You are now team members!');
          setActiveTab('members');
        } else {
          showNotification('neutral', 'Invitation declined.');
        }
      }
    } catch (e) { showNotification('error', 'Action failed'); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Remove this member? They will be removed from your team.")) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/team/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTeam(team.filter(m => m._id !== id));
    } catch (err) { showNotification('error', 'Failed to remove'); }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(' ');
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
  };

  if (loading) return <div className="flex h-full items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>;

  return (
    <div className={`flex-1 overflow-hidden flex flex-col ${darkMode ? 'text-white bg-primary' : 'text-slate-900 bg-slate-50'}`}>

      {/* HEADER */}
      <div className={`px-8 py-6 border-b shrink-0 flex flex-col md:flex-row md:items-center justify-between gap-4 ${darkMode ? 'bg-primary border-white/5' : 'bg-white border-slate-200'}`}>
        <div>
          <h1 className="text-2xl font-medium">Team</h1>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Manage members and permissions.</p>
        </div>

        {/* TABS */}
        <div className={`flex p-1 rounded-xl ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'members' ? (darkMode ? 'bg-white/10 text-white' : 'bg-white text-slate-900 shadow-sm') : 'text-slate-500'}`}
          >
            Members <span className="opacity-50 ml-1">{team.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'requests' ? (darkMode ? 'bg-white/10 text-white' : 'bg-white text-slate-900 shadow-sm') : 'text-slate-500'}`}
          >
            Requests
            {invites.length > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{invites.length}</span>}
          </button>
        </div>
      </div>

      {/* NOTIFICATION BANNER */}
      {notification.msg && (
        <div className={`text-center py-2 text-xs font-medium ${notification.type === 'error' ? 'bg-red-500 text-white' : notification.type === 'neutral' ? 'bg-slate-500 text-white' : 'bg-emerald-500 text-white'}`}>
          {notification.msg}
        </div>
      )}

      {/* CONTENT AREA */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 max-w-5xl mx-auto w-full">

        {/* --- MEMBERS TAB --- */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            {/* INVITE BAR */}
            <div className={`p-4 rounded-xl border flex flex-col sm:flex-row items-center gap-4 ${darkMode ? 'bg-slate-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex-1 w-full relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="email"
                  placeholder="Invite by email address..."
                  className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-sm outline-none transition-all ${darkMode ? 'bg-black/20 text-white focus:bg-black/40' : 'bg-slate-50 text-slate-900 focus:bg-slate-100'}`}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <button
                onClick={handleInvite}
                disabled={isInviting || !inviteEmail}
                className="w-full sm:w-auto px-6 py-2.5 bg-secondary hover:bg-secondary/90 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isInviting ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                <span>Invite</span>
              </button>
            </div>

            {/* MEMBER LIST (SLEEK) */}
            <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-slate-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              {team.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Users size={32} className="mx-auto mb-3 opacity-30" />
                  <p>You haven't added any team members yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-white/5">
                  {team.map(member => (
                    <div key={member._id} className="p-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-sm font-medium text-white shrink-0">
                        {getInitials(member.username)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">{member.username}</h4>
                        <p className="text-xs text-slate-500 truncate">{member.email}</p>
                      </div>
                      <div className="hidden sm:block px-3 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                        Active
                      </div>
                      <button
                        onClick={() => handleRemove(member._id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
                        title="Remove Member"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* --- REQUESTS TAB --- */}
        {activeTab === 'requests' && (
          <div className="max-w-2xl mx-auto">
            {invites.length === 0 ? (
              <div className="text-center py-20">
                <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center ${darkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                  <Bell size={24} className="text-slate-400" />
                </div>
                <h3 className="font-medium text-lg mb-1">No pending invites</h3>
                <p className="text-slate-500 text-sm">Any invitations sent to you will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invites.map(invite => (
                  <div key={invite._id} className={`p-5 rounded-2xl border flex flex-col sm:flex-row items-center gap-4 ${darkMode ? 'bg-slate-800/50 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
                    <div className="flex items-center gap-3 flex-1 w-full">
                      <div className="w-10 h-10 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                        <UserPlus size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          <span className="font-medium">{invite.sender?.username || 'Someone'}</span> invited you to their team.
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">{invite.sender?.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => handleRespond(invite._id, 'reject')}
                        className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-medium border transition-colors ${darkMode ? 'border-white/10 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`}
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => handleRespond(invite._id, 'accept')}
                        className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-medium bg-secondary hover:bg-secondary/90 text-white transition-colors"
                      >
                        Accept Invite
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}